/**
 * COEmailDialog — mirrors ProposalEmailDialog architecture so the mailto:
 * navigation fires from a FRESH user click on the "Send CO Link" button
 * with ZERO async awaits before it. This is the reliability pattern the
 * proposal email flow uses — Chrome silently drops mailto: navigations
 * when the browser's user-activation state has expired (which happens
 * after multiple sequential network awaits).
 *
 * Flow:
 *   1. User clicks the black "CO" button in COBuilder.
 *   2. Parent (WorkOrderTab) saves + submits the CO (mints approval token).
 *   3. Parent opens this dialog with the CO, token, and computed amount.
 *   4. User picks a recipient contact and clicks "Send CO Link".
 *   5. `onSend` fires `window.location.href = mailto:` synchronously.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, FileDown } from "lucide-react";
import { toast } from "sonner";
import { generateCOPdf } from "@/lib/coPdf";
import { EmailDocMode, useEmailDocMode, docFilename, withDocName } from "@/components/common/EmailDocMode";

export function COEmailDialog({
  open, onOpenChange,
  co,               // the submitted CO (or null while preparing)
  coAmount,         // live-computed total from COBuilder
  approvalToken,    // /co-review/{token}
  woNumber, woTitle,
  clientName, siteName, siteAddress,
  siteContacts,     // [{name, email, phone, is_primary}]
  taxRate,
  selectedContactEmail, setSelectedContactEmail,
  selectedContactId, setSelectedContactId,
}) {
  const fmt = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
  const docName = docFilename("Change_Order", co?.co_number);
  const reviewUrl = approvalToken ? withDocName(`${window.location.origin}/co-review/${approvalToken}`, docName) : "";
  const contacts = (siteContacts || []).filter(c => c.name || c.email);
  const [docMode, setDocMode] = useEmailDocMode();

  const handleSend = () => {
    // ZERO awaits before this — the CO was already submitted before the
    // dialog opened, and the mailto fires on THIS click as a fresh user
    // gesture. This mirrors Proposals.jsx::handleEmailProposal.
    if (!selectedContactEmail) {
      toast.error("Please select a recipient or enter an email address");
      return;
    }
    const attach = docMode === "attach";
    if (attach) handleDownloadPDF();
    const contact = contacts.find(c => c.email === selectedContactEmail);
    const firstName = contact?.name ? contact.name.split(" ")[0] : "";
    const greeting = firstName ? `Dear ${firstName},` : "Dear Customer,";

    const subject = encodeURIComponent(`Change Order ${co?.co_number || ""} — ${woNumber || ""} ${woTitle || ""}`);
    const body = encodeURIComponent(
      `${greeting}\n\n` +
      `A change order is ready for your review and approval:\n\n` +
      `Change Order: ${co?.co_number || ""}${co?.description ? ` — ${co.description}` : ""}\n` +
      `Work Order: ${woNumber || ""}${woTitle ? ` — ${woTitle}` : ""}\n` +
      `Change Order Amount: ${fmt(coAmount)}\n\n` +
      (attach
        ? `The change order (${docName}) is attached. Please review and let us know if you have any questions.\n\n`
        : `Please review, sign, and approve online here:\n${reviewUrl}\n\n` +
          `From this link you can view the change order details, download a PDF copy, and sign electronically to approve.\n\n` +
          `Please let us know if you have any questions.\n\n`) +
      `Thank you,\nAssociated Building Specialties, Inc.\n303-293-2096`
    );
    // FIRE MAILTO DIRECTLY — no setTimeout, no anchor click, no awaits.
    // Same exact pattern as `handleEmailProposal` at Proposals.jsx:575.
    window.location.href = `mailto:${selectedContactEmail}?subject=${subject}&body=${body}`;
    toast.success(attach ? "PDF downloaded — attach it to the email that just opened" : "Email opened with change order link");
    onOpenChange(false);
  };

  const handleDownloadPDF = async () => {
    if (!co) return;
    try {
      const doc = await generateCOPdf({
        co, woNumber, woTitle, clientName, siteName, siteAddress,
        taxRate, coTotal: coAmount, hideTax: !!co?.hide_tax,
      });
      doc.save(`${co.co_number || "ChangeOrder"}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="co-email-dialog">
        <DialogHeader><DialogTitle>Email Change Order {co?.co_number || ""}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <EmailDocMode mode={docMode} onChange={setDocMode} filename={docName} testId="co-email-doc-mode" />
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-0.5">
            <div><span className="font-medium text-slate-500">Change Order:</span> {co?.co_number || ""} {co?.description ? `— ${co.description}` : ""}</div>
            <div><span className="font-medium text-slate-500">Work Order:</span> {woNumber} {woTitle ? `— ${woTitle}` : ""}</div>
            <div><span className="font-medium text-slate-500">Amount:</span> <span className="font-semibold text-slate-800 tabular-nums">{fmt(coAmount)}</span></div>
          </div>

          <div>
            <Label className="text-xs font-medium">Send To</Label>
            {contacts.length > 0 ? (
              <div className="space-y-2 mt-2">
                <Select
                  value={
                    selectedContactId && contacts.some(c => (c.id || `idx-${contacts.indexOf(c)}`) === selectedContactId)
                      ? selectedContactId
                      : ""
                  }
                  onValueChange={(v) => {
                    setSelectedContactId(v);
                    const picked = contacts.find(c => (c.id || `idx-${contacts.indexOf(c)}`) === v);
                    setSelectedContactEmail(picked?.email || "");
                  }}
                >
                  <SelectTrigger className="w-full" data-testid="select-co-email-contact">
                    <SelectValue placeholder="Select a contact..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((contact, idx) => {
                      const stableId = contact.id || `idx-${idx}`;
                      return (
                        <SelectItem key={stableId} value={stableId} data-testid={`option-co-email-contact-${stableId}`}>
                          <span className="font-medium">{contact.name || "(unnamed)"}</span>
                          {contact.email && <span className="text-xs text-slate-500"> · {contact.email}</span>}
                          {!contact.email && <span className="text-xs text-rose-500"> · no email on file</span>}
                          {contact.is_primary && <span className="text-[10px] text-blue-600 font-medium"> (Primary)</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <div className="pt-2">
                  <Label className="text-xs">Or Enter Email Manually</Label>
                  <Input
                    type="email"
                    value={contacts.some(c => c.email === selectedContactEmail) ? "" : selectedContactEmail}
                    onChange={e => setSelectedContactEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="mt-1"
                    data-testid="input-co-manual-email"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
                  No saved contacts for this site. Enter a recipient email manually.
                </div>
                <Input
                  type="email"
                  value={selectedContactEmail}
                  onChange={e => setSelectedContactEmail(e.target.value)}
                  placeholder="email@example.com"
                  data-testid="input-co-manual-email"
                />
              </div>
            )}
          </div>

          <div className="flex justify-between items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="btn-download-co-pdf-dialog">
              <FileDown className="w-4 h-4 mr-1" /> Download PDF
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel-co-email">Cancel</Button>
              <Button
                onClick={handleSend}
                data-testid="btn-send-co-email"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Mail className="w-4 h-4 mr-1" /> Send CO Link
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
